import React, { createContext, useContext, useMemo } from "react";
import { useParams } from "react-router";
import { CherryPayApi } from "../api/models";
import { ErrorMessage } from "../components/ErrorMessage/ErrorMessage";
import { LoadingIndicator } from "../components/LoadingIndicator/LoadingIndicator";
import { ApiRequestState, useApiRequest } from "../hooks/useApiRequest";
import { useBusinessContext } from "./ModelContext";

interface TMemberContext {
  member: ApiRequestState<CherryPayApi.Member>;
}

const MemberContext = createContext<TMemberContext | null>(null);

/**
 * Loads the member for `:memberId`. Mount only after permission checks so the
 * member endpoint is not called for users without View Member Details.
 */
export const MemberContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const business = useBusinessContext();
  const { memberId } = useParams();
  const businessId = business.BusinessId;

  const memberRequest = useApiRequest(
    (apiClient) => {
      if (!businessId || !memberId) {
        return null;
      }
      return apiClient.getMember(businessId, memberId);
    },
    [businessId, memberId]
  );

  const context = useMemo(
    () => ({
      member: memberRequest,
    }),
    [memberRequest]
  );

  if (memberRequest.isLoading) {
    return <LoadingIndicator />;
  }

  if (memberRequest.error || !memberRequest.data) {
    return (
      <ErrorMessage>
        An error was encountered while loading the page
      </ErrorMessage>
    );
  }

  return (
    <MemberContext.Provider value={context}>{children}</MemberContext.Provider>
  );
};

/**
 * Returns the member loaded from the `:memberId` path parameter
 */
export const useMemberContext = (): CherryPayApi.Member => {
  const ctx = useContext(MemberContext);

  if (!ctx || !ctx.member.data) {
    throw new Error("Missing member context");
  }

  return ctx.member.data;
};
